import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../api';
import { IconApprove, IconReject, IconStar, IconFilm, IconLink, IconSuggest, IconClock } from '../components/Icons';

const FILTERS = ['pending','scheduled','rejected','posted'];

const IconTrash = ({ width=14, height=14, color='#ff4444' }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);

const SectionHeader = ({ label }) => (
  <div style={{
    fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'var(--text-muted)', marginBottom: 6, marginTop: 14, paddingBottom: 4,
    borderBottom: '1px solid var(--border)'
  }}>
    {label}
  </div>
);

function timeUntilDelete(rejectedAt) {
  if (!rejectedAt) return null;
  const remaining = new Date(rejectedAt).getTime() + 48*3600000 - Date.now();
  if (remaining <= 0) return 'Video deleted';
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  return h > 0 ? `Video deleted in ${h}h ${m}m` : `Video deleted in ${m}m`;
}

export default function Clips() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightRef = useRef(null);
  const [clips, setClips]             = useState([]);
  const [filter, setFilter]           = useState(searchParams.get('highlight') ? 'all' : (searchParams.get('status') || 'pending'));
  const [editing, setEditing]         = useState({});
  const [schedules, setSchedules]     = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [clipAnalytics, setClipAnalytics] = useState({});
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [clipsLoading, setClipsLoading] = useState(true);

  const fetchAnalytics = (clips) => {
    clips.filter(c => c.status === 'posted' && c.youtube_url).forEach(clip => {
      setClipAnalytics(a => ({...a, [clip.id]: a[clip.id] || 'loading'}));
      API.get(`/api/clips/${clip.id}/analytics`).then(ar => {
        setClipAnalytics(a => ({...a, [clip.id]: ar.data}));
      }).catch(()=> {
        setClipAnalytics(a => ({...a, [clip.id]: { views:0, likes:0, comments:0 }}));
      });
    });
  };

  const load = () => {
    const apiStatus = filter === 'scheduled' ? 'approved' : filter === 'all' ? '' : filter;
    API.get('/api/clips', { params: apiStatus ? { status: apiStatus } : {} }).then(r => {
      const filtered = r.data.filter(c => {
        if (c.status !== 'rejected') return true;
        if (!c.rejected_at) return true;
        return Date.now() - new Date(c.rejected_at).getTime() < 48*3600000;
      });
      setClips(filtered);
      fetchAnalytics(filtered);
      setClipsLoading(false);
    }).catch(()=>{ setClipsLoading(false); });
  };

  useEffect(() => { setClipsLoading(true); load(); }, [filter]);

  useEffect(() => {
    Promise.all([
      API.get('/api/analytics/insights'),
      API.get('/api/calendar', { params: { from: new Date().toISOString(), to: new Date(Date.now()+14*86400000).toISOString() }})
    ]).then(([ir, cr]) => {
      const schedule = ir.data?.insights?.weeklySchedule;
      if (!schedule) return;
      const taken = (cr.data||[]).map(e => { const d=new Date(e.scheduled_at); d.setMinutes(0,0,0); return d.getTime(); });
      const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const now = new Date();
      for (let i=0; i<14; i++) {
        const base = new Date(now.getTime()+i*86400000);
        const entry = schedule.find(d=>d.day===days[base.getDay()]);
        if (!entry?.times?.length) continue;
        for (const time of entry.times) {
          const [h,m] = time.split(':');
          const slot = new Date(base); slot.setHours(+h,+(m||0),0,0);
          const sr = new Date(slot); sr.setMinutes(0,0,0);
          if (slot>now && !taken.includes(sr.getTime())) {
            setSuggestions(s=>({...s, _default: new Date(slot.getTime()-slot.getTimezoneOffset()*60000).toISOString().slice(0,16)}));
            setSuggestionsLoading(false);
            return;
          }
        }
      }
      setSuggestionsLoading(false);
    }).catch(()=>{ setSuggestionsLoading(false); });
  }, []);

  const approve = async clip => {
    const title = editing[clip.id]?.title || clip.ai_title;
    const desc  = editing[clip.id]?.desc  || clip.ai_description;
    const sched = schedules[clip.id] || (suggestions._default ? new Date(suggestions._default).toISOString() : new Date(Date.now()+3600000).toISOString());
    await API.post(`/api/clips/${clip.id}/approve`, { custom_title:title, custom_description:desc, scheduled_at:sched });
    load();
  };

  const reject      = async id => { await API.post(`/api/clips/${id}/reject`); load(); };
  const deleteClip  = async id => { if (!window.confirm('Permanently delete? Cannot be undone.')) return; await API.delete(`/api/clips/${id}`); load(); };
  const changeStatus= async (id,status) => { await API.post(`/api/clips/${id}/status`, {status}); load(); };
  const setEdit     = (id,key,val) => setEditing(e=>({...e, [id]:{...(e[id]||{}), [key]:val}}));

  const scoreClass  = s => s>=7?'score-high':s>=5?'score-mid':'score-low';
  const fmt         = dt => new Date(dt).toLocaleString('en-GB',{weekday:'short',day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'});
  const displayStatus = s => (s==='approved'||s==='approved_auto')?'scheduled':s.replace('_',' ');
  const badgeClass    = s => (s==='approved'||s==='approved_auto')?'approved':s.replace('_auto','');
  const videoOk       = c => !c.rejected_at||Date.now()-new Date(c.rejected_at).getTime()<48*3600000;
  const isPosted      = c => c.status==='posted';
  const isPending     = c => c.status==='pending';

  return (
    <div>
      <div className="page-header" style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between'}}>
        <div>
          <div className="page-title">Clips</div>
          <div className="page-subtitle">Review, edit and approve clips for posting</div>
        </div>
        <a href="/upload" className="btn btn-ghost" style={{fontSize:13, display:'flex', alignItems:'center', gap:6, textDecoration:'none'}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
          Upload clip
        </a>
      </div>

      <div className="filter-tabs">
        {FILTERS.map(f => (
          <button key={f} className={`filter-tab${filter===f?' active':''}`} onClick={()=>setFilter(f)}>
            {f.charAt(0).toUpperCase()+f.slice(1)}
          </button>
        ))}
      </div>

      {filter==='rejected'&&clips.length>0&&(
        <div style={{background:'rgba(255,68,68,0.05)',border:'1px solid rgba(255,68,68,0.1)',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:13,color:'var(--text-muted)'}}>
          Rejected clips are automatically removed after 48 hours. Move them back to pending or delete manually before then.
        </div>
      )}

      {clipsLoading ? (
        <div className="empty-state">
          <div className="empty-icon" style={{marginBottom:16}}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1.2s linear infinite'}}>
              <rect x="2" y="2" width="20" height="20" rx="2.18"/>
              <line x1="7" y1="2" x2="7" y2="22"/>
              <line x1="17" y1="2" x2="17" y2="22"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <line x1="2" y1="7" x2="7" y2="7"/>
              <line x1="2" y1="17" x2="7" y2="17"/>
              <line x1="17" y1="17" x2="22" y2="17"/>
              <line x1="17" y1="7" x2="22" y2="7"/>
            </svg>
          </div>
          <div className="empty-desc" style={{color:'var(--text-muted)', fontSize:13}}>Loading clips...</div>
        </div>
      ) : clips.length===0?(
        <div className="empty-state">
          <div className="empty-icon"><IconFilm width={48} height={48}/></div>
          <div className="empty-title">No {filter === 'all' ? '' : filter} clips yet</div>
          <div className="empty-desc">{filter==='rejected'?'Rejected clips older than 48 hours are automatically removed':'Clips will appear here as your streams are monitored'}</div>
        </div>
      ):(
        <div className="clips-grid">
          {clips.map(clip=>(
            <div key={clip.id} ref={String(clip.id) === highlightId ? highlightRef : null} className="clip-card" >
              {clip.s3_url&&videoOk(clip)
                ?<video className="clip-video" src={clip.s3_url} controls/>
                :clip.thumbnail_url
                  ?<img className="clip-video" src={clip.thumbnail_url} alt="thumbnail" style={{objectFit:'cover'}}/>
                  :<div className="clip-video-placeholder"><IconFilm width={32} height={32} color="#444"/></div>
              }
              <div className="clip-body">

                {/* ── POSTED: clean minimal view ── */}
                {isPosted(clip) && (
                  <>
                    <div style={{fontFamily:'var(--font-display)',fontWeight:700,fontSize:14,marginBottom:10}}>
                      {clip.custom_title||clip.ai_title}
                    </div>
                    <div style={{display:'flex',gap:16,marginBottom:10,padding:'10px 12px',background:'var(--surface2)',borderRadius:8}}>
                      {clipAnalytics[clip.id] && clipAnalytics[clip.id] !== 'loading'
                        ? [['Views',clipAnalytics[clip.id].views],['Likes',clipAnalytics[clip.id].likes],['Comments',clipAnalytics[clip.id].comments]].map(([l,v])=>(
                            <div key={l} style={{textAlign:'center'}}>
                              <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:18,color:'var(--accent)'}}>{(v||0).toLocaleString()}</div>
                              <div style={{fontSize:11,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em'}}>{l}</div>
                            </div>
                          ))
                        : <div style={{fontSize:12,color:'var(--text-muted)'}}>Loading analytics...</div>
                      }
                    </div>
                    {clip.scheduled_at && (
                      <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--text-muted)',marginBottom:8}}>
                        <IconClock/>Posted: {fmt(clip.scheduled_at)}
                      </div>
                    )}
                    {clip.youtube_url && (
                      <a href={clip.youtube_url} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{width:'100%',marginTop:6,fontSize:13,display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                        <IconLink/> View on YouTube
                      </a>
                    )}
                  </>
                )}

                {/* ── NON-POSTED: sectioned view ── */}
                {!isPosted(clip) && (
                  <>
                    {/* Analysis */}
                    <SectionHeader label="Analysis" />
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                      <span className={`clip-score ${scoreClass(clip.ai_score)}`}><IconStar/> {clip.ai_score}/10</span>
                      <span className={`badge badge-${badgeClass(clip.status)}`}>{displayStatus(clip.status)}</span>
                    </div>
                    {clip.status==='rejected'&&clip.rejected_at&&(
                      <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6,display:'flex',alignItems:'center',gap:4}}>
                        <IconClock width={11} height={11}/>{timeUntilDelete(clip.rejected_at)}
                      </div>
                    )}
                    {clip.ai_reason && <div className="clip-reason" style={{marginBottom:0}}>"{clip.ai_reason}"</div>}

                    {/* Title */}
                    <SectionHeader label="Title" />
                    <input
                      defaultValue={clip.custom_title||clip.ai_title}
                      placeholder="Title"
                      style={{width:'100%',padding:'8px 10px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text)',fontFamily:'var(--font-body)',fontSize:13,outline:'none',marginBottom:0}}
                      onChange={e=>setEdit(clip.id,'title',e.target.value)}
                    />

                    {/* Description — not for rejected */}
                    {clip.status!=='rejected' && (
                      <>
                        <SectionHeader label="Description" />
                        <textarea
                          defaultValue={clip.custom_description||clip.ai_description}
                          placeholder="Description"
                          rows={2}
                          style={{width:'100%',padding:'8px 10px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text)',fontFamily:'var(--font-body)',fontSize:13,outline:'none',resize:'vertical',marginBottom:0}}
                          onChange={e=>setEdit(clip.id,'desc',e.target.value)}
                        />
                      </>
                    )}

                    {/* Schedule — only for pending */}
                    {isPending(clip) && (
                      <>
                        <SectionHeader label="Schedule to post" />
                        {suggestionsLoading ? (
                          <div style={{fontSize:12,color:'var(--text-muted)',display:'flex',alignItems:'center',gap:6,padding:'8px 0'}}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                            Finding best slot...
                          </div>
                        ) : (
                          <>
                            {suggestions._default && (
                              <div style={{fontSize:12,color:'var(--accent)',marginBottom:6,display:'flex',alignItems:'center',gap:6}}>
                                <IconSuggest/>Suggested: {fmt(suggestions._default)}
                              </div>
                            )}
                            <input
                              type="datetime-local"
                              defaultValue={suggestions._default||''}
                              style={{fontSize:12,color:'var(--text-dim)',width:'100%',padding:'8px 10px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6,outline:'none'}}
                              onChange={e=>setSchedules(s=>({...s,[clip.id]:new Date(e.target.value).toISOString()}))}
                            />
                          </>
                        )}
                      </>
                    )}

                    {/* Scheduled time for approved clips */}
                    {clip.status==='approved' && clip.scheduled_at && (
                      <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--text-muted)',marginTop:12}}>
                        <IconClock/>Scheduled: {fmt(clip.scheduled_at)}
                      </div>
                    )}



                    {/* Actions */}
                    {isPending(clip) && (
                      <div className="clip-actions" style={{marginTop:12}}>
                        <button className="btn btn-approve" onClick={()=>approve(clip)}><IconApprove/> Approve</button>
                        <button className="btn btn-danger" onClick={()=>reject(clip.id)}><IconReject/> Reject</button>
                      </div>
                    )}

                    {clip.status==='rejected' && (
                      <div style={{display:'flex',gap:8,marginTop:12}}>
                        <button className="btn btn-ghost" style={{flex:1,fontSize:13}} onClick={()=>changeStatus(clip.id,'pending')}>↩ Move back to pending</button>
                        <button className="btn btn-danger" style={{padding:'9px 12px'}} onClick={()=>deleteClip(clip.id)} title="Delete permanently"><IconTrash/></button>
                      </div>
                    )}

                    {clip.status==='approved' && (
                      <button className="btn btn-ghost" style={{width:'100%',marginTop:10,fontSize:13}} onClick={()=>changeStatus(clip.id,'pending')}>↩ Move back to pending</button>
                    )}
                  </>
                )}

              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
